import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;

function authHeadersWithOptions(options = {}) {
  const { includeJson = true } = options;
  const token = null;
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

export async function sendAiChat({ salonId, messages, context, supportContext, supportMode }) {
  if (!salonId) {
    throw new Error("SALON_ID_REQUIRED");
  }
  const payload = {
    messages,
    context: context || undefined,
    support_context: supportContext || undefined,
    support_mode: supportMode || undefined,
  };
  const res = await axios.post(`${API}/ai/chat`, payload, {
    headers: {
      ...authHeadersWithOptions(),
      "X-Salon-Id": String(salonId),
    },
  });
  return res.data;
}

export async function streamAiChat({
  salonId,
  messages,
  context,
  supportContext,
  supportMode,
  onEvent,
  signal,
}) {
  if (!salonId) {
    throw new Error("SALON_ID_REQUIRED");
  }
  const payload = {
    messages,
    context: context || undefined,
    support_context: supportContext || undefined,
    support_mode: supportMode || undefined,
  };
  const res = await fetch(`${API}/ai/chat/stream`, {
    method: "POST",
    headers: {
      ...authHeadersWithOptions(),
      "X-Salon-Id": String(salonId),
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    const errorText = await res.text();
    const err = new Error("AI_STREAM_FAILED");
    err.status = res.status;
    err.detail = errorText;
    throw err;
  }
  if (!res.body) {
    throw new Error("AI_STREAM_NO_BODY");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let donePayload = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const lines = part.split("\n");
      let eventType = "message";
      const dataLines = [];
      for (const line of lines) {
        if (line.startsWith("event:")) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      }
      const dataStr = dataLines.join("\n");
      let parsed = dataStr;
      if (dataStr) {
        try {
          parsed = JSON.parse(dataStr);
        } catch (err) {
          parsed = dataStr;
        }
      }
      if (typeof onEvent === "function") {
        onEvent({ event: eventType, data: parsed });
      }
      if (eventType === "done") {
        donePayload = parsed;
      }
    }
  }
  return donePayload;
}

export async function sendAiChatWithAttachments({
  salonId,
  messages,
  context,
  supportContext,
  supportMode,
  imageFile,
  docFile,
}) {
  if (!salonId) {
    throw new Error("SALON_ID_REQUIRED");
  }
  const payload = {
    messages,
    context: context || undefined,
    support_context: supportContext || undefined,
    support_mode: supportMode || undefined,
  };
  const form = new FormData();
  form.append("payload_json", JSON.stringify(payload));
  if (imageFile) {
    form.append("image", imageFile);
  }
  if (docFile) {
    form.append("file", docFile);
  }
  const res = await axios.post(`${API}/ai/chat-with-attachments`, form, {
    headers: {
      ...authHeadersWithOptions({ includeJson: false }),
      "X-Salon-Id": String(salonId),
    },
  });
  return res.data;
}

export async function getAiCapabilities({ salonId }) {
  if (!salonId) {
    throw new Error("SALON_ID_REQUIRED");
  }
  const res = await axios.get(`${API}/ai/capabilities`, {
    headers: {
      ...authHeadersWithOptions(),
      "X-Salon-Id": String(salonId),
    },
  });
  return res.data;
}

export async function getAiHealth() {
  const res = await axios.get(`${API}/ai/health`, {
    headers: authHeadersWithOptions(),
  });
  return res.data;
}

export async function exportAiTable({ salonId, exportToken, format }) {
  if (!salonId) {
    throw new Error("SALON_ID_REQUIRED");
  }
  const res = await axios.post(
    `${API}/ai/exports`,
    { format, export_token: exportToken },
    {
      headers: {
        ...authHeadersWithOptions(),
        "X-Salon-Id": String(salonId),
      },
      responseType: "blob",
      validateStatus: (status) => status < 500,
    }
  );
  return res;
}
